// ConsultationPackage/CommonOrderOptionsPicker.tsx

import React, {useEffect, useState} from 'react';
import PsychologistConsultationDateTimePicker from "Psychologist/PsychologistConsultationDateTimePicker";
import {FC, FR} from "WideLayout/Layouts";
import Button from "Core/components/elements/Button/Button";
import CircularProgress from "Core/components/elements/CircularProgress";
import {IPsychologist} from "../../Types/psychology/psychologist";
import {useSelector} from 'react-redux';
import {RootState} from 'Redux/store';
import {getConsultationTypeLabel} from "Locale/ruMap";
import {IConsultationConfig} from "../../Types/psychology/consultation";
import PriceByConsultationConfig from "Modules/ConsultationPackage/PriceByConsultationConfig";
import ConsultationConfigPicker from "Consultation/ConsultationConfigPicker";
import './order.css'
// Custom hook for debouncing a value
const useDebounce = <T, >(value: T, delay: number): T => {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

interface CommonOrderOptionsPickerProps {
    control: any;
    setValue: any;
    watch: any;
    psychologist: IPsychologist;
    onConfirm: () => void;
    isValid: boolean;
    selectedSlot: string | null;
    defaultSupervision?: boolean;
    defaultIsOffline?: boolean;
    hideSupervision?: boolean;
    hideOffline?: boolean;
    defaultConsultationConfigId?: number;
    defaultDateTimeSlot?: string;
}

const CommonOrderOptionsPicker: React.FC<CommonOrderOptionsPickerProps> = (
    {
        control,
        setValue,
        watch,
        psychologist,
        onConfirm,
        isValid,
        selectedSlot,
        defaultSupervision = false,
        defaultIsOffline = false,
        hideSupervision = false,
        hideOffline = false,
        defaultDateTimeSlot = undefined,
        defaultConsultationConfigId = undefined,
    }) => {
    // Отслеживаем выбранный конфиг
    const consultationConfig = watch('consultation_config');
    // Извлекаем длительность из выбранного конфига
    const duration = consultationConfig?.duration || null;
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Получаем статус и конфигурации из Redux
    const paramsStatus = useSelector((state: RootState) => state.psychologyParams.status);
    const consultationConfigs = useSelector((state: RootState) => state.psychologyParams.consultation_configs);

    const handleOnConfirm = async (e: any) => {
        setIsLoading(true);
        await onConfirm();
        setIsLoading(false);
    }
    // Функция для получения дефолтного ID конфигурации из localStorage
    const getDefaultConsultationConfigId = () => {
        if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
            return undefined;
        }

        try {
            const existingDataStr = localStorage.getItem('clientRequest');
            if (!existingDataStr) return undefined;

            const existingData = JSON.parse(existingDataStr);
            return existingData?.consultation_config?.id;
        } catch (error) {
            console.error('Ошибка при получении defaultConsultationConfigId:', error);
            return undefined;
        }
    };
    const defaultConsultationConfig = getDefaultConsultationConfigId();

    // Установка дефолтного конфигурационного ID
    useEffect(() => {
        if (defaultConsultationConfigId && consultationConfigs) {
            const defaultConfig = consultationConfigs.find(
                (config) => config.id === defaultConsultationConfigId
            );
            if (defaultConfig) {
                setValue('consultation_config', defaultConfig);
            } else {
                console.warn(`Consultation config with id ${defaultConsultationConfigId} not found.`);
            }
        }
    }, [defaultConsultationConfigId, consultationConfigs, setValue]);

    // Дебаунс выбранного конфига
    const debouncedConsultationConfig = useDebounce<IConsultationConfig | null>(consultationConfig, 50);

    // Проверка, нужно ли показывать PsychologistConsultationDateTimePicker
    const shouldShowDateTimePicker = !defaultDateTimeSlot && debouncedConsultationConfig && duration;

    // Если данные еще загружаются или произошла ошибка, показываем загрузку
    if (paramsStatus !== 'succeeded') {
        return (
            <div className="text-center">
                <CircularProgress size="50px"/>
            </div>
        );
    }

    return (
        <FC g={1}>
            {/* Если выбран дефолтный конфиг, показываем его параметры */}
            {defaultConsultationConfigId && consultationConfig
                ? <FC cls={'fs-5'}>
                    <FR>
                        <span>
                            {getConsultationTypeLabel(consultationConfig.type.name)}
                            &nbsp;
                            {consultationConfig.duration.value} минут
                        </span>
                    </FR>
                    {/* Отображаем другие параметры при необходимости */}
                    {defaultDateTimeSlot &&
                        <span>В {defaultDateTimeSlot}</span>
                    }
                </FC>
                : <FC>
                    <PriceByConsultationConfig
                        configId={debouncedConsultationConfig ? debouncedConsultationConfig.id : null}
                        currency="RUB"
                    />
                    <div className="cop-grid">
                        <ConsultationConfigPicker
                            cls={'gap-1'} // убрали cop-grid отсюда
                            defaultConsultationConfigId={defaultConsultationConfig}
                            psychologist={psychologist}
                            control={control}
                            setValue={setValue}
                            watch={watch}
                            defaultSupervision={defaultSupervision}
                            defaultIsOffline={defaultIsOffline}
                            hideSupervision={hideSupervision}
                            hideOffline={hideOffline}
                        />
                    </div>
                </FC>
            }

            {/* Выбор слота времени с дебаунсом */}
            {shouldShowDateTimePicker && (
                <PsychologistConsultationDateTimePicker
                    className={'my-2'}
                    psychologistId={psychologist.id}
                    duration={duration}
                    onConfirmSlot={(slot: string) => {
                        setValue('selected_slot', slot);

                        // Обновление reservation_paid в localStorage при выборе слота
                        const existingDataStr = localStorage.getItem('clientRequest');
                        const existingData = existingDataStr ? JSON.parse(existingDataStr) : {};
                        if (existingData.reservation_paid) {
                            // Сохранить существующее reservation_paid
                            localStorage.setItem('clientRequest', JSON.stringify(existingData));
                        }
                    }}
                />
            )}

            {/* Кнопка подтверждения */}
            <Button
                onClick={handleOnConfirm}
                variant={'contained'}
                loading={isLoading}
                disabled={!isValid || !selectedSlot}>
                Записаться
            </Button>
        </FC>
    );
};

export default CommonOrderOptionsPicker;
